import type { EpgInfo, MediaUrl, TvChannel } from '@setplex/tria-api'
import type { Effect, Store } from 'effector'
import type { IRewindMediaUrlParams } from '~/entities/player/tv/index.h'
import type { CurrentProgramMediaUrl } from '~/shared/interfaces'
import type { IWithRewoundMediaUrl } from '~/shared/interfaces/mediaUrl'

import { api } from '@setplex/tria-api'
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { model as router } from '~/core/router'
import { CategoryId, epgCategoriesModel } from '~/entities/epg/categories'
import { epgProgramsModel } from '~/entities/epg/programs'
import { legacyAnalytixSetHistory } from '~/features/analytix'
import { selectEpgCategoryModel } from '~/features/epg/categories/select'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { ONE_MINUTE_MS } from '~/shared/constants/time'
import { aye, exists, nay } from '~/shared/helpers'
import {
  getProgramByStamp,
  mergeUrlAndEpgSyncedInfo,
} from '~/widgets/epg/player/lib'

// init player
export const init = createEvent()

// reset widget on epg page left
export const left = createEvent()

// reset widget on sign out
export const reset = createEvent()

// fetch live url for channel
export const fetchUrl = createEvent()

// fetch rewind url for channel
export const fetchRewindUrl = createEvent<{
  startTime?: number
  rewindType?: string
}>()

// effects that fetch media url
export const loadChannelRewindUrlFx: Effect<
  IRewindMediaUrlParams,
  IWithRewoundMediaUrl | null
> = createEffect()
export const loadChannelLiveUrlFx: Effect<
  { channel: TvChannel },
  IWithRewoundMediaUrl | null
> = createEffect()

// update epg info of playing channel when current program ends
export const updateChannelEpg = createEvent()

export const $channelEpgCombined: Store<{
  channel: TvChannel | null
  epg: [Map<string, EpgInfo[]>]
}> = combine<{ channel: TvChannel | null; epg: [Map<string, EpgInfo[]>] }>({
  channel: selectEpgChannelModel.$active,
  epg: epgProgramsModel.$epg,
})

// store with playback player program
export const $currentEpgInfo = createStore<EpgInfo | null>(null).reset(
  selectEpgChannelModel.$active
)

sample({
  // set playback player program
  clock: $channelEpgCombined,
  source: { combined: $channelEpgCombined },
  filter: ({
    combined: {
      channel,
      epg: [programs],
    },
  }) => exists(channel) && exists(programs),
  fn: ({
    combined: {
      channel,
      epg: [programs],
    },
  }) => getProgramByStamp({ channel, programs, stamp: Date.now() }),
  target: $currentEpgInfo,
})

sample({
  // update playback player with next program
  clock: updateChannelEpg,
  source: { combined: $channelEpgCombined, currentEpg: $currentEpgInfo },
  filter: ({
    combined: {
      channel,
      epg: [programs],
    },
  }) => exists(channel) && exists(programs),
  fn: ({
    combined: {
      channel,
      epg: [programs],
    },
    currentEpg,
  }) => {
    const nextProgramStamp = currentEpg
      ? currentEpg.actualStopMs + ONE_MINUTE_MS
      : Date.now()
    return getProgramByStamp({ channel, programs, stamp: nextProgramStamp })
  },
  target: $currentEpgInfo,
})

loadChannelLiveUrlFx.use(async ({ channel }) => {
  if (!channel) return null

  const rewindUrl: MediaUrl | null = await api.player.loadChannelUrlFx({
    channelId: channel.id,
  })
  if (!rewindUrl?.playbackUrl) return null

  return {
    ...rewindUrl,
    rewoundUrlStartSec: 0,
    rewoundTargetStartSec: 0,
  }
})

loadChannelRewindUrlFx.use(
  async ({ channel, urlStartTime, targetStartTime, rewindType }) => {
    if (!targetStartTime || !urlStartTime || !channel) return null

    const rewindUrl: MediaUrl | null = await api.player.loadChannelUrlFx({
      channelId: channel.id,
      startTime: urlStartTime,
      rewindType,
    })
    if (!rewindUrl?.playbackUrl) return null
    return {
      ...rewindUrl,
      rewoundUrlStartSec: urlStartTime,
      rewoundTargetStartSec: targetStartTime,
    }
  }
)

// currently playing channel id and available flag
export const $playing = createStore<{ id: number; available: boolean } | null>(
  null
)

// currently selected channel url
//
// Reset on `loadChannelLiveUrlFx` (a live-url fetch starting) so a channel
// switch/reload clears the previous channel's url instead of keeping it on
// screen until the next url settles. This mirrors the non-EPG
// `$currentPlayerPageUrl`, which resets on its fetch effects. Without it the
// synced media data briefly pairs the OLD url with the NEW channel/program
// (`$currentEpgInfo` resets and repopulates from cache faster than the fetch
// completes), which shows a "last shot" of the previous stream and can trigger
// a stray reload of the channel we just left.
export const $url = createStore<IWithRewoundMediaUrl | null>(null)
  .on(
    [loadChannelLiveUrlFx.doneData, loadChannelRewindUrlFx.doneData],
    (_, payload) => payload
  )
  .reset(
    loadChannelLiveUrlFx,
    api.player.loadChannelUrlFx.fail,
    api.session.signInFx.done
  )

const $categoryName = combine(
  {
    categories: epgCategoriesModel.$loaded,
    categoryId: selectEpgCategoryModel.$activeId,
  },
  ({ categories, categoryId }) => {
    if (categoryId == null) return null

    let categoryName = ''

    if (categoryId === CategoryId.Favorite) categoryName = 'Favorite'
    else if (categoryId === CategoryId.Featured) categoryName = 'Featured'
    else if (categoryId === CategoryId.All) categoryName = 'All'

    if (!categoryName) {
      categoryName = categories.find((c) => c.id === categoryId)?.name || 'All'
    }

    return categoryName
  }
)

// As epg page do not go new page to show player, provide data to analytix manually
sample({
  clock: [$url, $categoryName],
  source: {
    categoryName: $categoryName,
    url: $url,
  },
  filter: ({ categoryName, url }) => aye(url) && aye(categoryName),
  fn: ({ categoryName }) => ({
    history: {
      module: 'Epg',
      categoryName: categoryName!,
      routeName: undefined,
    },
  }),
  target: legacyAnalytixSetHistory,
})

// Sync epg category with URL query for analytix categoryName and openFrom fields.
sample({
  clock: [init, $categoryName],
  source: {
    categoryName: $categoryName,
    pathname: router.$pathname,
  },
  filter: ({ categoryName, pathname }) =>
    aye(categoryName) && aye(pathname?.startsWith('/modules/epg')),
  fn: ({ categoryName }) => ({ categoryName: categoryName! }),
  target: router.navigateReplaceQuery,
})

// clear url data if channel locked
sample({
  clock: selectEpgChannelModel.$isPinLocked,
  filter: (isPinLocked) => aye(isPinLocked),
  target: $url.reinit,
})

export const $playerProgramUrlSyncedInfo: Store<CurrentProgramMediaUrl | null> =
  combine(
    {
      mediaUrlData: $url,
      channel: selectEpgChannelModel.$active,
      program: $currentEpgInfo,
    },
    ({ mediaUrlData, channel, program }) => {
      if (!mediaUrlData || !channel || !program) return null

      return mergeUrlAndEpgSyncedInfo({ mediaUrlData, channel, program })
    }
  )

// on new channel select -> update currently playing channel
// also playing channel be updated if it becomes available after purchase
sample({
  clock: selectEpgChannelModel.$active,
  source: $playing,
  filter: (playing, active) => {
    if (active == null) return false
    // `available` flag will be changed, if channel id is the same, but was purchased
    const available = aye(active.free) || aye(active.purchaseInfo)
    return playing?.id !== active.id || playing?.available !== available
  },

  fn: (_, active) => ({
    id: active!.id,
    available: aye(active!.free) || aye(active!.purchaseInfo),
  }),
  target: $playing,
})

// if selected new channel -> request to load playback url
sample({
  clock: $playing,
  filter: Boolean,
  fn: ({ id }) => ({ channelId: id }),
  target: fetchUrl,
})

// fetch live url, if channel available
sample({
  clock: [fetchUrl, selectEpgChannelModel.$isPinLocked],
  source: {
    channel: selectEpgChannelModel.$active,
    isPinLocked: selectEpgChannelModel.$isPinLocked,
  },
  filter: ({ channel, isPinLocked }) => exists(channel) && nay(isPinLocked),
  fn: ({ channel }) => ({ channel: channel! }),
  target: loadChannelLiveUrlFx,
})

// fetch rewind url, if channel available
sample({
  clock: fetchRewindUrl,
  source: selectEpgChannelModel.$active,
  filter: (channel) => exists(channel),
  fn: (channel, clock) => ({
    channel: channel!,
    ...clock,
  }),
  target: loadChannelRewindUrlFx,
})

//
// left and reset logic
//

sample({
  clock: [left, reset],
  target: [$playing.reinit, $url.reinit],
})
